import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useLocation, useSearchParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {useUser} from "../../providers/user-context";
import axios from "../../config/axios-config";
import {eventBus} from "../../event-bus";

export const ORDER_QUEUES = ["ALL", "NEEDS_REVIEW", "AWAITING_CONFIRMATION", "READY_FOR_DISPATCH", "DELIVERY_ISSUES", "OUTSTANDING"];
export const ORDER_STATUSES = [null, "PENDING", "PROCESSING", "SHIPPED", "COMPLETED", "CANCELLED"];
const validDate = value => !value || (/^\d{4}-\d{2}-\d{2}$/.test(value)
    && Number.isFinite(Date.parse(`${value}T00:00:00Z`)) && new Date(`${value}T00:00:00Z`).toISOString().slice(0, 10) === value);

export function orderQueueFilters(params) {
    const status = params.get("status"), queue = params.get("queue"), page = Number(params.get("page") || 1);
    return {search: params.get("q") || "", status: ORDER_STATUSES.includes(status) ? status : null,
        queue: ORDER_QUEUES.includes(queue) ? queue : "ALL", start: params.get("from") || "", end: params.get("to") || "",
        page: Number.isSafeInteger(page) && page > 0 ? page - 1 : 0};
}

export const validOrderQueueFilters = filters => validDate(filters.start) && validDate(filters.end)
    && (!filters.start || !filters.end || filters.start <= filters.end) && filters.search.length <= 200;

function validPage(data) {
    return Array.isArray(data?.content) && Number.isSafeInteger(data.totalPages) && data.totalPages >= 0
        && Number.isSafeInteger(data.totalElements) && data.totalElements >= 0
        && ORDER_QUEUES.every(queue => queue === "OUTSTANDING" && data.counts?.[queue] === undefined
            || Number.isSafeInteger(data.counts?.[queue]) && data.counts[queue] >= 0);
}

export default function useOrderQueue(role) {
    const location = useLocation(), [params, setParams] = useSearchParams(), {i18n} = useTranslation(), user = useUser();
    const filters = useMemo(() => orderQueueFilters(params), [params]), valid = validOrderQueueFilters(filters);
    const identity = [user.tenantId, user.userIdentity || user.userFullName, user.tenantType, user.userRole].join(":");
    const scope = `${identity}:${i18n.language}:${location.pathname}:${location.key}:${JSON.stringify(filters)}`;
    const current = useRef(scope); current.current = scope;
    const [state, setState] = useState({}), [version, setVersion] = useState(0), [searchDraft, setSearchDraft] = useState({});
    const searchText = searchDraft.key === location.key ? searchDraft.value : filters.search;
    const refresh = useCallback(() => setVersion(value => value + 1), []);
    useEffect(() => {
        // History can return to an earlier location key; do not resurrect the draft typed on that visit.
        setSearchDraft({key: location.key, value: filters.search});
    }, [location.key, identity, filters.search]);
    const update = useCallback((changes, replace = false) => {
        const next = new URLSearchParams(params), values = {...filters, ...changes};
        for (const [key, value] of Object.entries({q: values.search, status: values.status, queue: values.queue === "ALL" ? null : values.queue,
            from: values.start, to: values.end, page: changes.page === undefined ? null : values.page ? values.page + 1 : null})) {
            if (value) { next.set(key, String(value)); } else { next.delete(key); }
        }
        setParams(next, {replace});
    }, [params, filters, setParams]);
    useEffect(() => {
        if (searchText === filters.search) { return; }
        const timer = setTimeout(() => update({search: searchText.trim()}), 350);
        return () => clearTimeout(timer);
        // Navigating cancels a pending search, including navigation to a notification or another identity.
    }, [searchText, filters.search, location.key, identity]);
    useEffect(() => {
        eventBus.on("refreshOrderPage", refresh);
        return () => eventBus.off("refreshOrderPage", refresh);
    }, [refresh]);
    useEffect(() => {
        const controller = new AbortController();
        if (!valid || user.userLoading) { return () => controller.abort(); }
        setState(old => ({...(old.scope === scope ? old : {}), scope, loading: true, error: ""}));
        axios.get(`/api/v1/orders/${role}/queue`, {params: {page: filters.page, size: 10, queue: filters.queue,
            searchTerm: filters.search || null, status: filters.status,
            startDate: filters.start ? `${filters.start}T00:00:00` : null,
            endDate: filters.end ? `${filters.end}T23:59:59.999999` : null},
            headers: {"Language-Context": i18n.language.toUpperCase()}, signal: controller.signal, timeout: 15000})
            .then(({data}) => {
                if (!validPage(data)) { throw new Error("Invalid order queue page"); }
                if (current.current === scope && !controller.signal.aborted) { setState({scope, loading: false, data}); }
            }).catch(error => {
                if (current.current === scope && !controller.signal.aborted) {
                    setState(old => ({...old, scope, loading: false, error: error.response?.data?.message || "OrderQueueLoadFailed"}));
                }
            });
        return () => controller.abort();
        // The scope contains every applied filter, identity, language and navigation fence.
    }, [scope, role, valid, version, location.state?.refresh, user.userLoading]);
    const setSearchText = useCallback(value => setSearchDraft({key: location.key, value}), [location.key]);
    const clear = useCallback(() => {
        setSearchDraft({key: location.key, value: ""}); update({search: "", status: null, queue: "ALL", start: "", end: ""});
    }, [location.key, update]);
    // Dialog-local state must not rebuild an otherwise unchanged background table.
    return useMemo(() => ({...(state.scope === scope ? state : {loading: true}), filters, valid, identity,
        searchText, refresh, update, setSearchText, clear}),
    [state, scope, filters, valid, identity, searchText, refresh, update, setSearchText, clear]);
}
